const mk_calendarweek = (() => {
    const DEBUG = false;
    const VERSION = "2.1";

    const config = {
        baseWeek: 0,
        variations: {},
        currentWeek: null,
        selectBox: null,
        productForm: null,
        statusBox: null,
        weeksToShow: 24,
        text: {
            asap: "Schnellstmöglich (4-5 Werktage)",
            holidays: "Lieferung ab KW 02 | Nach den Feiertagen",
            inStock: "Auf Lager – jetzt versandfertig",
            notInStock: "Hohe Nachfrage – Auslieferung ab KW",
        }
    };

    const state = {
        lastVariant: 0,
        activeWeek: 0,
    };

    const qs = (selector, scope = document) => scope.querySelector(selector);

    const log = (...args) => {
        if (DEBUG) console.log("📅", ...args);
    };

    const normalizeWeek = (value) => {
        if (value === null || value === undefined || value === "") {
            return 0;
        }

        const parsed = Number(value);

        if (!Number.isInteger(parsed) || parsed < 0) {
            return 0;
        }

        return parsed;
    };

    const isNonDeliverableWeek = (weekNumber, year) => {
        const lastWeek = getWeeksInYear(year);
        return weekNumber === 1 || weekNumber === lastWeek || weekNumber === lastWeek - 1;
    };

    const updateDeliveryStatus = () => {
      if (!config.statusBox) return;
      const raw = config.selectBox?.value ?? "";
      const week = parseInt(raw.replace(/^KW/, ""), 10);
      
      const statusBoxAlert = qs(".alert", config.statusBox);
      const statusBoxSpan = qs("span", config.statusBox);

      if (!statusBoxAlert || !statusBoxSpan) {
        log("No ALERT/SPAN for statusbox found!");
        return;
      }

      if (week > 0) {
          statusBoxSpan.textContent = `${config.text.notInStock} ${String(week).padStart(2, "0")}`;
          statusBoxAlert.classList.remove('alert--success')
          statusBoxAlert.classList.add('alert--info');
          return;
      }

      statusBoxSpan.textContent = config.text.inStock;
      statusBoxAlert.classList.remove('alert--info');
      statusBoxAlert.classList.add('alert--success');
    };

    const createOptionElement = ({ label, value }) => {
        const option = document.createElement("option");
        option.textContent = label;
        option.value = value;
        return option;
    };

    const clearSelectBox = () => {
        if (config.selectBox) {
            config.selectBox.innerHTML = "";
        }
    };

    const renderOptions = (options) => {
        if (!config.selectBox) return;

        clearSelectBox();

        const fragment = document.createDocumentFragment();

        options.forEach((option) => {
            fragment.appendChild(createOptionElement(option));
        });

        config.selectBox.appendChild(fragment);
    };

    const getISOWeekNumber = (input = new Date()) => {
        const date = new Date(Date.UTC(
            input.getFullYear(),
            input.getMonth(),
            input.getDate()
        ));

        const isoDay = date.getUTCDay() || 7;
        date.setUTCDate(date.getUTCDate() + 4 - isoDay);

        const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));

        return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
    };

    const getWeeksInYear = (year) => {
        const december28 = new Date(Date.UTC(year, 11, 28));
        return getISOWeekNumber(december28);
    };

    const getDateRangeOfWeek = function (weekNumber, year) {
        const simple = new Date(year, 0, 1 + (weekNumber - 1) * 7);
        const dayOfWeek = simple.getDay();
        const monday = new Date(simple);

        monday.setDate(simple.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1));

        const friday = new Date(monday);

        friday.setDate(monday.getDate() + 4);

        return {
            start: monday.toLocaleDateString('de-DE', { day: 'numeric', month: 'numeric' }),
            end: friday.toLocaleDateString('de-DE', { day: 'numeric', month: 'numeric', year: '2-digit'}),
            startTimestamp: monday.getTime(),
        };
    };

    const getNextDeliverableWeeks = (startWeek, count = config.weeksToShow) => {
        const weeks = [];
        let year = new Date().getFullYear();
        let week = startWeek;

        while (weeks.length < count) {
            const weeksInYear = getWeeksInYear(year);

            if (week > weeksInYear) {
                week = 1;
                year += 1;
                continue;
            }

            if (!isNonDeliverableWeek(week, year)) {
                const dateRange = getDateRangeOfWeek(week, year);

                weeks.push({
                    label: `KW${String(week).padStart(2, "0")} (${dateRange.start} - ${dateRange.end})`,
                    value: `KW${String(week).padStart(2, "0")}`,
                    timestamp: dateRange.startTimestamp,
                    week,
                    year,
                });
            }

            week += 1;
        }

        return weeks;
    };

    const getDefaultOption = () => {
        if (isNonDeliverableWeek(config.currentWeek, new Date().getFullYear())) {
            return {
                label: config.text.holidays,
                value: "KW02",
            };
        }

        return {
            label: config.text.asap,
            value: "",
        };
    };

    const getEarliestSelectableWeek = () => {
        return config.currentWeek + 1;
    };

    const isFutureWeek = (week) => {
        return week > config.currentWeek;
    };

    const resolveTargetWeek = (baseWeek, variantWeek) => {
        const target = Math.max(baseWeek, variantWeek);
        return isFutureWeek(target) ? target : 0;
    };

    const buildOptionsForWeek = (week) => {
        const normalizedWeek = normalizeWeek(week);

        if (normalizedWeek === 0) {
            return [
                getDefaultOption(),
                ...getNextDeliverableWeeks(getEarliestSelectableWeek()),
            ];
        }

        return getNextDeliverableWeeks(normalizedWeek);
    };

    const updateCalendarweekOptions = (week) => {
        state.activeWeek = normalizeWeek(week);
        const options = buildOptionsForWeek(state.activeWeek);
        renderOptions(options);
        updateDeliveryStatus();
    };

    const changeCalendarweek = (variantId) => {
        const variantWeek = normalizeWeek(config.variations[variantId]);
        const targetWeek = resolveTargetWeek(config.baseWeek, variantWeek);

        if (targetWeek === state.activeWeek) {
            log("No change needed");
            return;
        }

        log("Change week to", targetWeek);

        updateCalendarweekOptions(targetWeek);
    };

    const bindEvents = function () {
        config.selectBox.addEventListener('change', function() {
            if (this.dataset.instock == 'true') {
                this.classList.add('instock');
            }
        });

        if (config.productForm) {
            config.productForm.addEventListener('change', function () {
                const variantInput = qs('input[name="id"]', config.productForm);
                const currentVariantId = variantInput?.value;

                if (!currentVariantId || currentVariantId === state.lastVariant) return;

                state.lastVariant = currentVariantId;

                changeCalendarweek(currentVariantId);

                updateDeliveryStatus();
            });
        }
    };

    const init = function (base = 0, variant = 0, variations = {}) {
        const normalizedBase = normalizeWeek(base);
        const normalizedVariant = normalizeWeek(variant);

        config.baseWeek = normalizedBase;
        config.variations = variations;
        config.currentWeek = getISOWeekNumber();
        config.selectBox = qs("#calendarweek-select");
        config.productForm = qs('form[action*="/cart/add"]');
        config.statusBox = qs("#delivery-status");

        log("Initializing calendarweek", {
            version: VERSION,
            base: normalizedBase,
            variant: normalizedVariant,
            currentWeek: config.currentWeek,
            variations,
        });

        const initialWeek = resolveTargetWeek(normalizedBase, normalizedVariant);
        
        bindEvents();
        updateCalendarweekOptions(initialWeek);
    };

    return { init };
})();